"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

// Convert moment.js format to date-fns format
function convertMomentToDateFns(momentFormat: string): string {
  return momentFormat
    .replace(/DD/g, "dd")
    .replace(/YYYY/g, "yyyy")
    .replace(/YY/g, "yy")
}

interface DatePickerProps {
  date?: Date | string
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  fromYear?: number
  toYear?: number
  dateFormat?: string
  id?: string
  name?: string
  onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void
  onChange?: (e: React.ChangeEvent<HTMLButtonElement>) => void
}

export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      date,
      onDateChange,
      placeholder = "Pick a date",
      className,
      disabled = false,
      fromYear = 1900,
      toYear = new Date().getFullYear(),
      dateFormat = "PPP",
      id,
      name,
      onBlur,
    },
    ref
  ) => {
    const toValidDate = (value?: Date | string): Date | undefined => {
      if (!value) return undefined
      const parsed = typeof value === "string" ? new Date(value) : value
      return isNaN(parsed.getTime()) ? undefined : parsed
    }

    const [open, setOpen] = React.useState(false)
    const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
      toValidDate(date)
    )

    // Convert moment format to date-fns format if needed
    const dateFnsFormat = dateFormat.includes("DD") || dateFormat.includes("YYYY")
      ? convertMomentToDateFns(dateFormat)
      : dateFormat

    React.useEffect(() => {
      setSelectedDate(toValidDate(date))
    }, [date])

    const handleSelect = (newDate: Date | undefined) => {
      setSelectedDate(newDate)
      if (onDateChange) {
        onDateChange(newDate)
      }
      if (newDate) {
        setOpen(false)
      }
    }

    return (
      <Popover modal={true} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            id={id}
            name={name}
            variant="outline"
            disabled={disabled}
            onBlur={onBlur}
            className={cn(
              "w-full justify-start text-left font-normal",
              !selectedDate && "text-muted-foreground",
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedDate ? (
              format(selectedDate, dateFnsFormat)
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={selectedDate}
            onSelect={handleSelect}
            startMonth={new Date(fromYear, 0)}
            endMonth={new Date(toYear, 11)}
            disabled={(day) =>
              day > new Date() || day < new Date(fromYear, 0, 1)
            }
            initialFocus
          />
        </PopoverContent>
      </Popover>
    )
  }
)

DatePicker.displayName = "DatePicker"
